// ─────────────────────────────────────────────
// KLOTHIQ — Confirm Dialog Context & HOC / Hook
// Programmatic, promise-based confirmation modals
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Info, Trash2, X } from 'lucide-react';

export interface ConfirmOptions {
  title?: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'destructive' | 'warning' | 'primary';
  /** When set, the dialog renders a text input and Confirm stays disabled
   *  until the typed value exactly matches this string (case-sensitive).
   *  For irreversible actions (delete) where a plain click-through
   *  Confirm button isn't enough friction — see FEAT-MASTER-CONFIG-DELETE-001. */
  confirmInputValue?: string;
  confirmInputLabel?: string;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
}

// A mounted provider and a hot-updated consumer must share the same context object.
// Vite persists hot.data between evaluations of this module; production creates it once.
const ConfirmContext =
  (import.meta.hot?.data.confirmContext as
    | React.Context<ConfirmContextType | undefined>
    | undefined) ?? createContext<ConfirmContextType | undefined>(undefined);
if (import.meta.hot) import.meta.hot.data.confirmContext = ConfirmContext;

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);
  const [inputValue, setInputValue] = useState('');
  const returnFocus = useRef<HTMLElement | null>(null);

  const confirm = useCallback((options: ConfirmOptions | string): Promise<boolean> => {
    const parsedOptions: ConfirmOptions =
      typeof options === 'string' ? { description: options } : options;

    return new Promise<boolean>((resolve) => {
      returnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setInputValue('');
      setDialogState({
        isOpen: true,
        options: parsedOptions,
        resolve,
      });
    });
  }, []);

  const handleClose = (result: boolean) => {
    if (dialogState) {
      dialogState.resolve(result);
      setDialogState(null);
      setInputValue('');
    }
  };

  const requiredInput = dialogState?.options.confirmInputValue;
  const isConfirmDisabled = !!requiredInput && inputValue !== requiredInput;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialogState && dialogState.isOpen && (
        <Dialog.Root
          open
          onOpenChange={(value) => {
            if (!value) handleClose(false);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" data-field-guide-scope />
            <Dialog.Content
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                if (returnFocus.current?.isConnected) returnFocus.current.focus();
              }}
              className="fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  {dialogState.options.variant === 'destructive' && (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
                      <Trash2 className="h-5 w-5" />
                    </div>
                  )}
                  {dialogState.options.variant === 'warning' && (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                  )}
                  {(!dialogState.options.variant || dialogState.options.variant === 'primary') && (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Info className="h-5 w-5" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-start justify-between gap-3 pr-8">
                      <Dialog.Title className="text-base font-semibold text-foreground">
                        {dialogState.options.title ?? 'Confirm Action'}
                      </Dialog.Title>
                      <UserGuide compact />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  aria-label="Close confirmation"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3">
                <Dialog.Description className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                  {dialogState.options.description}
                </Dialog.Description>
              </div>

              {requiredInput && (
                <div className="mt-4">
                  <label
                    htmlFor="global-confirm-input"
                    className="mb-1.5 block text-xs font-medium text-foreground"
                  >
                    {dialogState.options.confirmInputLabel ?? (
                      <>
                        Type <span className="font-mono font-semibold">{requiredInput}</span> to
                        confirm
                      </>
                    )}
                  </label>
                  <Input
                    type="text"
                    id="global-confirm-input"
                    autoFocus
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isConfirmDisabled) handleClose(true);
                    }}
                    placeholder={requiredInput}
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    helpLabel="Confirmation text"
                  />
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="min-h-11 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
                >
                  {dialogState.options.cancelText ?? 'Cancel'}
                </button>
                <button
                  type="button"
                  disabled={isConfirmDisabled}
                  onClick={() => handleClose(true)}
                  className={`min-h-11 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed ${
                    dialogState.options.variant === 'destructive'
                      ? 'bg-destructive hover:bg-destructive/90'
                      : dialogState.options.variant === 'warning'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  }`}
                >
                  {dialogState.options.confirmText ?? 'Confirm'}
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
}

/**
 * Higher-Order Component (HOC) version of confirm
 */
export function withConfirm<P extends object>(
  Component: React.ComponentType<
    P & { confirm: (options: ConfirmOptions | string) => Promise<boolean> }
  >,
) {
  return function WrappedWithConfirm(props: P) {
    const confirm = useConfirm();
    return <Component {...props} confirm={confirm} />;
  };
}
